import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import api from '../../services/api';

interface Profile {
  name: string;
  avatar: string | null;
  tier: string;
  is_seller: boolean;
  is_verified: boolean;
  phone: string;
  rating_avg: number | string;
  ads_count: number;
}

const NAV: { to: string; icon: string; label: string }[] = [
  { to: '/my/ads', icon: '📋', label: 'إعلاناتي' },
  { to: '/my/favorites', icon: '❤️', label: 'المفضلة' },
  { to: '/my/conversations', icon: '💬', label: 'المحادثات' },
  { to: '/my/notifications', icon: '🔔', label: 'الإشعارات' },
  { to: '/commission', icon: '💰', label: 'عمولاتي' },
  { to: '/my/featured', icon: '⭐', label: 'الترقية للتميز' },
  { to: '/my/support', icon: '🎧', label: 'تذاكر الدعم' },
  { to: '/my/profile', icon: '⚙️', label: 'الإعدادات' },
];

const tierLabel = (p: Profile): string => {
  if (!p.is_seller) return 'عضو عادي';
  return p.tier === 'company_seller' ? 'بائع — شركة' : 'بائع — فرد';
};

const maskPhone = (ph: string): string => {
  if (!ph) return '';
  const d = ph.replace(/\D/g, '');
  if (d.length < 6) return ph;
  return '+966 ' + d.slice(-9, -7) + ' *** ' + d.slice(-4);
};

const AccountLayout: React.FC = () => {
  const loc = useLocation();
  const [p, setP] = useState<Profile | null>(null);

  useEffect(() => {
    api.get('/profile').then(r => setP(r.data?.data || null)).catch(() => {});
  }, []);

  const isActive = (to: string) => loc.pathname === to || loc.pathname.startsWith(to + '/');

  return (
    <>
      <style>{css}</style>
      <div className="acc">
        <aside className="acc-side">
          <div className="acc-profile">
            <div className="acc-avatar">
              {p?.avatar ? <img src={p.avatar} alt="" /> : (p?.name?.charAt(0) || '؟')}
            </div>
            <div className="acc-name">{p?.name || '...'}</div>
            <div className="acc-tier">
              {p && <span className="acc-badge-tier">{tierLabel(p)}</span>}
              {p?.is_verified && <span className="acc-badge-v">✓ موثّق</span>}
            </div>
            {p?.phone && <div className="acc-phone" dir="ltr">{maskPhone(p.phone)}</div>}
            <div className="acc-stats">
              <div><b>{p?.ads_count ?? 0}</b><span>إعلان</span></div>
              <div><b>{p ? Number(p.rating_avg || 0).toFixed(1) : '0.0'}</b><span>⭐ تقييم</span></div>
            </div>
            <Link to="/my/profile" className="acc-edit">✏️ تعديل الملف</Link>
          </div>

          <nav className="acc-nav">
            {NAV.map(n => (
              <Link key={n.to} to={n.to} className={'acc-nav-item' + (isActive(n.to) ? ' is-active' : '')}>
                <span className="acc-nav-icon">{n.icon}</span>
                <span>{n.label}</span>
              </Link>
            ))}
          </nav>
        </aside>

        <main className="acc-main">
          <Outlet />
        </main>
      </div>
    </>
  );
};

const css = `
.acc{max-width:1180px;margin:20px auto;padding:0 16px;display:flex;gap:20px;align-items:flex-start;font-family:var(--aj-font)}
.acc *{box-sizing:border-box}
.acc-side{width:270px;flex-shrink:0;position:sticky;top:20px;display:flex;flex-direction:column;gap:14px}
.acc-profile{background:#fff;border:1px solid var(--aj-border);border-radius:var(--aj-radius);padding:20px;text-align:center;box-shadow:var(--aj-shadow)}
.acc-avatar{width:72px;height:72px;border-radius:50%;background:var(--aj-primary);color:#fff;display:flex;align-items:center;justify-content:center;font-size:28px;font-weight:800;margin:0 auto 12px;overflow:hidden}
.acc-avatar img{width:100%;height:100%;object-fit:cover}
.acc-name{font-size:16px;font-weight:800;color:var(--aj-text)}
.acc-tier{display:flex;gap:6px;justify-content:center;flex-wrap:wrap;margin-top:6px}
.acc-badge-tier{background:var(--aj-primary-50);color:var(--aj-primary);font-size:11px;font-weight:700;padding:2px 10px;border-radius:9999px}
.acc-badge-v{background:#EFF6FF;color:#3B82F6;font-size:11px;font-weight:700;padding:2px 10px;border-radius:9999px}
.acc-phone{font-size:12px;color:var(--aj-muted);margin-top:8px}
.acc-stats{display:flex;justify-content:center;gap:22px;margin:14px 0;padding:12px 0;border-top:1px solid var(--aj-border);border-bottom:1px solid var(--aj-border)}
.acc-stats div{display:flex;flex-direction:column}
.acc-stats b{font-size:17px;font-weight:800;color:var(--aj-primary)}
.acc-stats span{font-size:11px;color:var(--aj-muted)}
.acc-edit{display:block;background:#fff;border:1.5px solid var(--aj-primary);color:var(--aj-primary);border-radius:var(--aj-radius-sm);padding:9px;font-size:13px;font-weight:700;text-decoration:none}
.acc-edit:hover{background:var(--aj-primary-50)}
.acc-nav{background:#fff;border:1px solid var(--aj-border);border-radius:var(--aj-radius);padding:8px;box-shadow:var(--aj-shadow);display:flex;flex-direction:column;gap:2px}
.acc-nav-item{display:flex;align-items:center;gap:10px;padding:11px 14px;border-radius:var(--aj-radius-sm);text-decoration:none;color:var(--aj-text-2);font-size:14px;font-weight:600;transition:all .15s}
.acc-nav-item:hover{background:var(--aj-bg)}
.acc-nav-item.is-active{background:var(--aj-primary-50);color:var(--aj-primary);font-weight:800}
.acc-nav-icon{width:22px;text-align:center;font-size:15px}
.acc-main{flex:1;min-width:0}
@media(max-width:900px){
  .acc{flex-direction:column}
  .acc-side{width:100%;position:static;flex-direction:column}
  .acc-nav{flex-direction:row;overflow-x:auto;padding:6px}
  .acc-nav-item{white-space:nowrap;flex-shrink:0}
  .acc-nav-item span:not(.acc-nav-icon){display:none}
  .acc-nav-icon{font-size:18px}
}
`;

export default AccountLayout;
